import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  PlaidAccount,
  PlaidLinkOnExit,
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink as usePlaidLinkSetup,
} from "react-plaid-link";
import { useNavigate } from "react-router-dom";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import { companyMetadataSelector, userMetadataSelector } from "store/selectors";

import { AppEvents } from "services/events";
import { BankAccountVerifyStatus } from "types/BankAccountVerifyStatus";
import { DefaultBankAccountType, ErrorConst } from "types/BETypes";
import { PlaidExitErrors } from "types/plaid";
import { HELP_EMAIL } from "constants/shared";
import { hasAdminRights } from "permissions/helpers/shared";
import { showErrorModal } from "helpers/index";
import { getErrorMessageFromResponse } from "helpers/shared/errors";

import { EButtonsFlex, EModalTypes } from "uikit/Modal";

import {
  GetCompanyByIdResponseDto,
  GetPlaidBankAccountsResponseDto,
  mutationPlaidControllerChangeToken,
  mutationPlaidControllerDeleteBankAccount,
  mutationPlaidControllerRenewLoginBankAccount,
  mutationPlaidControllerVerifyBankAccount,
  PlaidControllerGetLinkTokenQueryParams,
  queryPlaidControllerGetLinkToken,
  queryPlaidControllerListBankAccounts,
  UserProfileResponseDto,
} from "utils/swagger_react_query";

import useAuth from "./useAuth";

/**
 * <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
 * need to be added to index.html
 */

type ISuccessActionCallback = (
  bankAccountsList?: GetPlaidBankAccountsResponseDto[],
  newBankAccount?: PlaidAccount[],
  oldBankAccountsList?: GetPlaidBankAccountsResponseDto[],
) => void;

interface IParams {
  shouldFetchBankAccountsListOnMount?: boolean;
  loadingCallback: (state: boolean) => void;
  onSuccessAddedCallback?: ISuccessActionCallback;
  onSuccessVerifiedCallback?: ISuccessActionCallback;
  handleDetermineDefaultBankAccount?: (
    currentCompany: GetCompanyByIdResponseDto | null,
    bankAccountsList: GetPlaidBankAccountsResponseDto[],
  ) => void;
}

export interface IUseBankAccountLinkReturn {
  metadata: {
    currentUser: UserProfileResponseDto | null | undefined;
    isReady: boolean;
  };
  data: {
    bankAccounts: GetPlaidBankAccountsResponseDto[] | undefined;
  };
  actions: {
    openModal: () => Promise<void>;
    refetchBankAccountsList: (
      withLoading?: boolean,
    ) => Promise<GetPlaidBankAccountsResponseDto[] | undefined>;
    handleRemoveBankAccount: (
      bankAccountId: string,
      withConfirmationModal?: boolean,
    ) => Promise<void>;
    handleVerifyBankAccount: (bankAccountId: string, accessToken: string) => Promise<void>;
  };
}

export const useBankAccountLink = ({
  shouldFetchBankAccountsListOnMount = true,
  handleDetermineDefaultBankAccount,
  ...params
}: IParams): IUseBankAccountLinkReturn => {
  const componentsPrefix = "components.bank_account_components.external_bank_account_modals";

  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const { getCompanyInfo } = useAuth();
  const [plaidPublicToken, setPlaidPublicToken] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] = useState<GetPlaidBankAccountsResponseDto[] | undefined>(
    undefined,
  );
  const userIsAdmin = hasAdminRights(currentUser);

  //NOTE:::It is used to verify bank account after exit from Plaid Link
  const [targetBankAccountId, setTargetBankAccountId] = useState<string | null>(null);

  //NOTE:::Plaid Configuration
  const _fetchPlaidLinkToken = async (accessToken?: string) => {
    try {
      let payload: PlaidControllerGetLinkTokenQueryParams | undefined;
      if (accessToken) {
        payload = {
          accessToken,
        };
      }

      const linkTokenResponse = await queryPlaidControllerGetLinkToken(payload);
      setPlaidPublicToken(linkTokenResponse.linkToken);
    } catch (error) {
      console.log(error);
      showErrorModal(error);
    }
  };

  const _onSuccessVerificationModal = (
    bankAccountsListRes: GetPlaidBankAccountsResponseDto[] | undefined,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    if (params?.onSuccessVerifiedCallback)
      return params?.onSuccessVerifiedCallback?.(
        bankAccountsListRes,
        metadata.accounts,
        bankAccounts,
      );

    return AppEvents.emit("SetGlobalModal", {
      isOpen: true,
      type: EModalTypes.SUCCESS,
      title: t(`${componentsPrefix}.success_verified.title`),
      message: t(`${componentsPrefix}.success_verified.message`),
      customContentStyle: { width: "560px" },
      mainButton: {
        text: t(`buttons.done`),
      },
    });
  };

  const _onSuccessRefetchCompanyInfo = async (
    bankAccountsListRes: GetPlaidBankAccountsResponseDto[] | undefined,
  ) => {
    const companyInfoRes = await getCompanyInfo(currentCompany?.companyId ?? "", true);
    const isDefaultBankAccountExternal =
      companyInfoRes?.defaultBankAccountType === DefaultBankAccountType.EXTERNAL_ACCOUNT;

    if (isDefaultBankAccountExternal) {
      handleDetermineDefaultBankAccount?.(companyInfoRes || null, bankAccountsListRes || []);
    }
  };

  const _onSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
    try {
      const isVerificationFlow =
        metadata.accounts[0].verification_status === BankAccountVerifyStatus.MANUALLY_VERIFIED;

      const shouldRefetchCompanyInfo =
        userIsAdmin &&
        (bankAccounts?.length === 0 || (bankAccounts?.length === 1 && isVerificationFlow));
      const isNeedRenewBankAccountLogin = (bankAccounts || []).find(
        (it) => it.bankAccountId === targetBankAccountId,
      )?.renewLoginRequired;
      params?.loadingCallback(true);

      if (isVerificationFlow) {
        const _bankAccountId = bankAccounts?.find(
          (it) => it?.plaidAccountId === metadata.accounts[0].id,
        )?.bankAccountId;
        if (!_bankAccountId) throw new Error("");

        await mutationPlaidControllerVerifyBankAccount({ accountId: _bankAccountId })();
      } else if (isNeedRenewBankAccountLogin) {
        await mutationPlaidControllerRenewLoginBankAccount({
          accountId: targetBankAccountId ?? "",
        })();
      } else {
        await mutationPlaidControllerChangeToken()({
          publicToken,
        });
      }

      const bankAccountsListRes = await fetchBankAccountsList();
      if (shouldRefetchCompanyInfo) {
        _onSuccessRefetchCompanyInfo(bankAccountsListRes);
      }

      if (isVerificationFlow) {
        _onSuccessVerificationModal(bankAccountsListRes, metadata);
      }

      if (params?.onSuccessAddedCallback)
        return params?.onSuccessAddedCallback?.(
          bankAccountsListRes,
          metadata.accounts,
          bankAccounts,
        );

      return AppEvents.emit("SetGlobalModal", {
        isOpen: true,
        type: EModalTypes.SUCCESS,
        title: t(`${componentsPrefix}.success_added.title`),
        message: t(`${componentsPrefix}.success_added.message`),
        customContentStyle: { width: "560px" },
        mainButton: {
          text: t(`buttons.done`),
        },
      });
    } catch (error: any) {
      params?.loadingCallback(false);
      const _errorMessage = getErrorMessageFromResponse(error);
      if (_errorMessage === ErrorConst.BANK_ACCOUNT_LIMIT_EXCEEDED) {
        showErrorModal(_errorMessage, undefined, {
          type: EModalTypes.ERROR,
          message: <Trans i18nKey={`errors.${_errorMessage}`} values={{ helpEmail: HELP_EMAIL }} />,
        });
      } else {
        showErrorModal();
      }
    } finally {
      setTargetBankAccountId(null);
      sessionStorage.clear();
    }
  };

  const _onExit: PlaidLinkOnExit = async (error, metadata) => {
    try {
      if (error?.error_code === PlaidExitErrors.TOO_MANY_VERIFICATION_ATTEMPTS) {
        params.loadingCallback(true);
        if (!targetBankAccountId) throw new Error("");
        await mutationPlaidControllerVerifyBankAccount({ accountId: targetBankAccountId })();
        await fetchBankAccountsList();
      }
    } catch (error) {
      showErrorModal(error);
    } finally {
      setTargetBankAccountId(null);
    }
  };

  const config: PlaidLinkOptions = {
    token: plaidPublicToken,
    onSuccess: _onSuccess,
    onExit: _onExit,
  };

  const { open, ready } = usePlaidLinkSetup(config);

  useEffect(() => {
    if (ready) open();
  }, [ready, open]);

  useEffect(() => {
    if (shouldFetchBankAccountsListOnMount) {
      fetchBankAccountsList();
    }
  }, []);

  const fetchBankAccountsList = async (withLoading: boolean = true) => {
    try {
      if (withLoading) {
        params?.loadingCallback(true);
      }
      const existingBankAccounts = await queryPlaidControllerListBankAccounts();
      setBankAccounts(existingBankAccounts || []);

      return existingBankAccounts;
    } catch (error) {
      console.log(error);
    } finally {
      if (withLoading) {
        params?.loadingCallback(false);
      }
    }
  };

  const handleRemoveBankAccount = async (
    bankAccountId: string,
    withConfirmationModal: boolean = true,
  ) => {
    const _removeBankAccount = async (bankAccountId: string) => {
      try {
        params?.loadingCallback(true);
        await mutationPlaidControllerDeleteBankAccount({ accountId: bankAccountId })();
        await fetchBankAccountsList();
      } catch (error) {
        params?.loadingCallback(false);
        showErrorModal(error);
      }
    };

    if (withConfirmationModal) {
      const isBankAccountInUse =
        !userIsAdmin &&
        currentUser?.activeCompanyUser?.defaultPayDistribution?.some(
          (it) => it.id === bankAccountId,
        );
      if (isBankAccountInUse) {
        return AppEvents.emit("SetGlobalModal", {
          isOpen: true,
          type: EModalTypes.WARNING,
          title: t(`${componentsPrefix}.bank_in_use_delete_modal.title`),
          message: t(`${componentsPrefix}.bank_in_use_delete_modal.message`),
          mainButton: {
            text: t(`${componentsPrefix}.bank_in_use_delete_modal.btn`),
            onClick: () => navigate(routes.EMPLOYEE_SETTINGS_PAY_DISTRIBUTION),
          },
          secondaryButton: {
            text: t("buttons.cancel"),
          },
          buttonsFlex: EButtonsFlex.ROW_REVERSE,
          customContentStyle: { width: "560px" },
        });
      } else {
        return AppEvents.emit("SetGlobalModal", {
          isOpen: true,
          type: EModalTypes.DELETE,
          title: t(`${componentsPrefix}.remove_conformation.title`),
          message: t(`${componentsPrefix}.remove_conformation.message`),
          mainButton: {
            text: t(`buttons.delete`),
            onClick: () => {
              _removeBankAccount(bankAccountId);
            },
          },
          customContentStyle: { width: "560px" },
          secondaryButton: {
            text: t(`buttons.cancel`),
            onClick: () => {
              AppEvents.emit("SetGlobalModal", { isOpen: false });
            },
          },
          buttonsFlex: EButtonsFlex.ROW_REVERSE,
        });
      }
    }

    _removeBankAccount(bankAccountId);
  };

  const openPlaidModal = async () => {
    await _fetchPlaidLinkToken();
  };

  const handleVerifyBankAccount = async (bankAccountId: string, accessToken: string) => {
    await _fetchPlaidLinkToken(accessToken);
    setTargetBankAccountId(bankAccountId);
  };

  return {
    metadata: {
      currentUser,
      isReady: ready,
    },
    data: {
      bankAccounts,
    },
    actions: {
      openModal: openPlaidModal,
      refetchBankAccountsList: fetchBankAccountsList,
      handleRemoveBankAccount,
      handleVerifyBankAccount,
    },
  };
};
